import { Element } from 'html-react-parser';
import type { DOMNode } from 'html-react-parser';

import { planIncontentSlots } from './snack-slots';
import type { BodyBlockKind, PlannedSlotId } from './snack-slots';

/**
 * Marks the placeholder element withAdSlotPlaceholders adds where a slot goes,
 * for the WordPress article page's replace callback to swap for SnackAdSlot.
 */
const AD_SLOT_PLACEHOLDER_ATTRIBUTE = 'data-snack-ad-slot';

const TEXT_ELEMENT_NAMES = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'blockquote'
]);

const MEDIA_ELEMENT_NAMES = new Set([
  'iframe',
  'img',
  'picture',
  'video',
  'figure',
  'script'
]);

const getText = (node: DOMNode): string => {
  if (node.type === 'text') return (node as unknown as { data: string }).data;
  if (node instanceof Element) return node.children.map(getText).join('');
  return '';
};

const containsMedia = (element: Element): boolean =>
  element.children.some(
    (child) =>
      child instanceof Element &&
      (MEDIA_ELEMENT_NAMES.has(child.name) || containsMedia(child))
  );

const isSignificant = (node: DOMNode): boolean =>
  node.type !== 'comment' &&
  !(node.type === 'text' && getText(node).trim() === '');

const getNodeKind = (node: DOMNode): BodyBlockKind => {
  if (!(node instanceof Element)) return 'text';
  if (TEXT_ELEMENT_NAMES.has(node.name)) return 'text';
  if (node.name !== 'p' || containsMedia(node)) return 'other';
  return getText(node).trim() ? 'paragraph' : 'text';
};

/**
 * A WordPress post body's top-level nodes with a placeholder wherever one of
 * Snack's in-content slots (FFH-3481) or the video player (FFH-3483) goes, by
 * the same rule as Sanity articles. Whitespace between elements is ignored, so
 * it can't make an image look like text when deciding whether a slot may sit
 * next to it.
 */
export const withAdSlotPlaceholders = (nodes: DOMNode[]): DOMNode[] => {
  const significantNodes = nodes.filter(isSignificant);
  const slots = planIncontentSlots(significantNodes.map(getNodeKind), {
    shouldPlaceVideo: true
  });
  if (slots.length === 0) return nodes;

  const slotIdsByNode = new Map<DOMNode, PlannedSlotId>(
    slots.map(({ afterIndex, slotId }) => [
      significantNodes[afterIndex],
      slotId
    ])
  );

  return nodes.flatMap((node) => {
    const slotId = slotIdsByNode.get(node);
    if (!slotId) return [node];
    return [
      node,
      new Element('div', { [AD_SLOT_PLACEHOLDER_ATTRIBUTE]: slotId })
    ];
  });
};

/** The slot a placeholder from withAdSlotPlaceholders stands for. */
export const getAdSlotPlaceholderId = (
  node: DOMNode
): PlannedSlotId | undefined =>
  node instanceof Element
    ? (node.attribs[AD_SLOT_PLACEHOLDER_ATTRIBUTE] as PlannedSlotId)
    : undefined;
